import * as React from 'react';
const { useState, useEffect, useCallback } = React;

const loadingPhrases = {
  default: [
    'Accès aux données publiques...',
    'Analyse des budgets institutionnels...',
    'Chargement des visualisations...',
    'Vérification des sources officielles...',
    'Préparation des données fiscales...',
  ],
  analysis: [
    'Extraction des données budgétaires...',
    'Analyse des flux financiers publics...',
    'Calcul des indicateurs de transparence...',
    'Comparaison avec les années précédentes...',
    'Élaboration des graphiques analytiques...',
  ],
  dataConnect: [
    'Connexion aux bases de données nationales...',
    'Récupération des documents officiels...',
    'Synchronisation des données ministérielles...',
    'Vérification des sources institutionnelles...',
    'Mise à jour des dernières publications...',
  ],
  guest: [
    'Configuration de l\'accès visiteur...',
    'Préparation des fonctionnalités de démonstration...',
    'Chargement des analyses d\'exemple...',
    'Configuration de l\'accès limité...',
    'Création d\'une session temporaire...',
  ],
};

const loadingStates = {
  default: [
    'Initialisation d\'AI France Revolution...',
    'Connexion aux bases de données...',
    'Chargement des analyses budgétaires...',
    'Vérification des sources officielles...',
    'Finalisation de la configuration...',
  ],
  guest: [
    'Création de votre session visiteur...',
    'Préparation de l\'accès limité...',
    'Chargement des fonctionnalités de démonstration...',
    'Configuration de l\'environnement de test...',
    'Finalisation de la configuration visiteur...',
  ],
};

const LOADING_TIMEOUT = 30000; // 30 seconds

const LoadingScreen = ({
  type = 'default',
  error = null,
  customMessage = '',
  onTimeout = () => {},
  isGuest = false,
}) => {
  const [phrase, setPhrase] = useState('');
  const [progress, setProgress] = useState(0);
  const [currentState, setCurrentState] = useState(0);

  const updatePhrase = useCallback(() => {
    const phrases = loadingPhrases[isGuest ? 'guest' : type] || loadingPhrases.default;
    setPhrase(phrases[Math.floor(Math.random() * phrases.length)]);
  }, [type, isGuest]);

  const updateLoadingState = useCallback(() => {
    const states = isGuest ? loadingStates.guest : loadingStates.default;
    setCurrentState((prevState) =>
      prevState < states.length - 1 ? prevState + 1 : prevState
    );
  }, [isGuest]);

  useEffect(() => {
    updatePhrase();
    const phraseInterval = setInterval(updatePhrase, 3000);
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = prevProgress + Math.random() * 4;
        return newProgress > 100 ? 100 : newProgress;
      });
    }, 200);
    const stateInterval = setInterval(updateLoadingState, 3000);

    const timeoutId = setTimeout(() => {
      onTimeout();
    }, LOADING_TIMEOUT);

    return () => {
      clearInterval(phraseInterval);
      clearInterval(progressInterval);
      clearInterval(stateInterval);
      clearTimeout(timeoutId);
    };
  }, [type, isGuest, customMessage, updatePhrase, updateLoadingState, onTimeout]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
        <div className="bg-white dark:bg-slate-800 p-8 rounded-lg border border-slate-200 dark:border-slate-700 shadow-xl max-w-md w-full">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Erreur</h2>
          <p className="text-slate-700 dark:text-slate-300 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const currentLoadingStates = isGuest ? loadingStates.guest : loadingStates.default;

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="bg-white dark:bg-slate-800 p-8 rounded-lg border border-slate-200 dark:border-slate-700 shadow-xl max-w-md w-full">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-indigo-500 animate-spin"></div>
        </div>
        
        <h2 className="text-2xl font-bold text-center text-slate-800 dark:text-white mb-4">
          {progress >= 100 ? 'Presque terminé...' : 'Chargement...'}
        </h2>
        
        <p className="text-indigo-600 dark:text-indigo-400 text-center mb-2">{phrase}</p>
        
        <p className="text-slate-500 dark:text-slate-400 text-center text-sm mb-4">
          {customMessage ||
            (progress >= 100 ? 'Dernières retouches...' : currentLoadingStates[currentState])}
        </p>
        
        <div className="w-full h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mb-4">
          <div 
            className="h-full bg-indigo-600 dark:bg-indigo-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <p className="text-slate-500 dark:text-slate-400 text-center text-sm">
          {isGuest
            ? 'Veuillez patienter pendant que nous préparons votre expérience visiteur...'
            : 'Veuillez patienter pendant que nous connectons aux bases de données institutionnelles...'}
        </p>
      </div>
    </div>
  );
};

export default LoadingScreen;